import React, { useState, useRef, useEffect } from 'react';
import TitleBar from '@/components/layout/TitleBar';
import { useParams } from 'react-router-dom';
import { Button, Grid, Typography } from '@mui/material';
import SignatureCanvas from 'react-signature-canvas'
import { RESOURCE_TYPE, UPLOAD_COLLECTION_METHOD } from '@/stack-shared/constants/resource-constants';
import { StandardUploadInput } from '@/components/form/StandardUploadInput';
import FullScreenLoader from '@/components/shared/FullScreenLoader';
import { getFileExtension } from '@/utils/general';
import { useUploadResourceFile } from '@/hooks/api/useResources';
import { useCreateLocationReport, useUpdateLocationReport } from '@/hooks/api/useReports';
import { useNotification } from '@/contexts/NotificationContext';
import { useLocationDetails } from '@/hooks/useLocation';
import { IResource } from '@/stack-shared/interfaces/model/resource.interfaces';
import { useGenerateReport } from '@/hooks/useLocation';
import { createRoot } from 'react-dom/client';
import { lazy, Suspense } from 'react';

// Create a separate component for PDF generation
const PDFGenerator = lazy(() => import('./PDFGenerator'));

const LocationReportPage: React.FC = () => {
  const { locationId } = useParams();
  const { data: locationDetails } = useLocationDetails(locationId || '');
  const location = locationDetails?.location;
  const [disableButton, setDisableButton] = useState(true);
  const [image, setImage] = useState<File|null>(null);
  const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);
  const [html, setHtml] = useState('');
  const [reportIsSaved, setReportIsSaved] = useState(false);
  const sigCanvas = useRef<SignatureCanvas | null>(null);
  const generateReportMutation = useGenerateReport();
  const uploadResourceFileMutation = useUploadResourceFile();
  const createLocationReportMutation = useCreateLocationReport();
  const updateLocationReportMutation = useUpdateLocationReport();
  const { showSuccess, showError } = useNotification();
  const [breadcrumbs, setBreadcrumb] = useState([
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Service', href: '/app/location-service' },
    { label: location?.name || 'Location', href: `/app/location-service/${locationId}` },
    { label: 'Location Reports', href: `/app/location-service/${locationId}/reports` },
    { label: 'Generate Report' },  // Current page
  ]);

  const [reportId, setReportId] = useState(null);

  const generateReport = (sendToPrimaryContact: boolean = false) => {
    console.log("REPORT ID", reportId);
   
    setShowFullScreenLoader(true);
    generateReportMutation.mutate({
      locationId: Number(locationId),
      reportId: reportId || undefined,
      sendToPrimaryContact,
    }, {
      onSuccess: (data) => {

        console.log("Data from report?", data)
        if (data && data.html) {
          if (sendToPrimaryContact) {
            showSuccess('Report sent to primary contact successfully.');
          } else {
            showSuccess('Report generated successfully.');
            // Convert HTML to PDF and download
            const element = document.createElement('div');
            element.innerHTML = data.html;

            // Wait for all images to load before generating PDF
            const images = element.getElementsByTagName('img');
            const imagePromises = Array.from(images).map(img => {
              return new Promise((resolve, reject) => {
                if (img.complete) {
                  resolve(null);
                } else {
                  img.onload = () => resolve(null);
                  img.onerror = reject;
                }
              });
            });

            Promise.all(imagePromises)
              .then(() => {
                // Render the PDFGenerator component
                const pdfGenerator = document.createElement('div');
                pdfGenerator.id = 'pdf-generator';
                document.body.appendChild(pdfGenerator);
                const root = createRoot(pdfGenerator);
                root.render(<PDFGenerator html={data.html} locationId={locationId} />);
                // Clean up after PDF generation
                setTimeout(() => {
                  root.unmount();
                  document.body.removeChild(pdfGenerator);
                }, 1000);
              })
              .catch(error => {
                console.error('Error loading images:', error);
                showError('Error generating PDF. Some images may not be included.');
              });
          }
        } else {
          showError('Failed to generate report.');
        }
        setShowFullScreenLoader(false);
      },
      onError: (error) => {
        console.error('Failed to generate report:', error);
        enqueueSnackbar('Failed to generate report.', { variant: 'error' });
        setShowFullScreenLoader(false);
      }
    });
  };
  
  const previewReport = () => {
    generateReportMutation.mutate({
      locationId: Number(locationId),
      preview: true,
    }, {
      onSuccess: (data) => {
        setHtml(data.html);
      },
    onError: (error) => {
        console.error('Failed to preview report:', error);
        showError('Failed to preview report.');
      }
    });
  };

  const updateReport = (signatureResource: IResource, identityResource: IResource | null, generatedReportId: number) => {
    updateLocationReportMutation.mutate({ locationId, reportId: generatedReportId, signatureResource, identityResource }, {
      onSuccess: () => {
        setShowFullScreenLoader(false);
        setReportIsSaved(true);
      },
      onError: (error: any) => {
        showError(error.message || 'Failed to update report');
      },
    });
  }

  const handleGenerateReport = (generatedReportId: number) => {
    console.log("GENERATED REPORT ID", generatedReportId);
    setShowFullScreenLoader(true);
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      try {
        const trimmedDataURL = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
        const signatureFile = dataURLtoFile(trimmedDataURL, 'signature.png');
        const type = 'signature';
        const uploadData = {
          name: type,
          model: 'report',
          modelId: generatedReportId,
          secondaryModel: 'location',
          secondaryModelId: locationId,
          context: type,
          extension: getFileExtension(signatureFile!.name),
          fileName: `${type}-${locationId}.${getFileExtension(signatureFile!.name)}`,
          originalFileName: signatureFile!.name,
          uploadCollectionMethod: UPLOAD_COLLECTION_METHOD.REPLACE,
          type: RESOURCE_TYPE.IMAGE,
          parts: 1,
          file: signatureFile,
          rootPath: 'location-report/' + generatedReportId,
          primary: true,
          conversions: [],
          fileSize: signatureFile!.size
        };

        uploadResourceFileMutation.mutate(uploadData, {
          onSuccess: (data) => {
            showSuccess('Signature uploaded successfully.');
            const signatureResource = data.resource;
            console.log({ data }, 'signature image');
            if (image) {
              const typeImage = 'identity';
              const uploadDataImage = {
                name: typeImage,
                model: 'report',
                modelId: generatedReportId,
                secondaryModel: 'location',
                secondaryModelId: locationId,
                context: typeImage,
                extension: getFileExtension(image!.name),
                fileName: `${typeImage}-${locationId}.${getFileExtension(image!.name)}`,
                originalFileName: image!.name,
                uploadCollectionMethod: UPLOAD_COLLECTION_METHOD.REPLACE,
                type: RESOURCE_TYPE.IMAGE,
                parts: 1,
                file: image,
                rootPath: 'location-report/' + generatedReportId,
                primary: true,
                conversions: [],
                fileSize: image!.size
              };

              uploadResourceFileMutation.mutate(uploadDataImage, {
                onSuccess: (identityData) => {
                  showSuccess('Image uploaded successfully.');
                  const identityResource = identityData.resource;
                  console.log({ identityData }, 'identity image');
                  updateReport(signatureResource, identityResource, generatedReportId);
                },
                onError: (error: any) => {
                  showError(error.message || 'Failed to upload image');
                },
              });

            } else {
              updateReport(signatureResource, null, generatedReportId);
            }
          },
          onError: (error: any) => {
            showError(error.message || 'Failed to upload signature');
          },
        });

      } catch (error) {
        setShowFullScreenLoader(false);
        console.error("Error trimming signature:", error);
        showError('Invalid signature or image.');
      }
    }

  }

  const dataURLtoFile = (dataUrl: string, filename: string): File => {
    const arr = dataUrl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };


  const clearSignature = () => {
    sigCanvas.current?.clear();
    setDisableButton(true);
  }

  const saveReport = () => {
    createLocationReportMutation.mutate({ locationId, content: html }, {
      onSuccess: (data) => {
        showSuccess('Report saved successfully.');
        setReportId(data.report.id);
        handleGenerateReport(data.report.id);
      },
      onError: (error: any) => {
        showError(error.message || 'Failed to save report');
      },
    });
  }

  useEffect(() => {
    previewReport();
  }, [location]);

  return (
    <>
      <FullScreenLoader open={showFullScreenLoader} />
      <TitleBar
        breadcrumbs={breadcrumbs}
        title={`${location?.name || 'Location'} Report`}
        rightContent={null}
      />

      <Grid container width="100%">
        <Grid size={{ xs: 12, sm: 12, md: 12 }}>
          <div dangerouslySetInnerHTML={{ __html: html }} />
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 12 }}>
          <Typography variant="subtitle2" style={{ marginBottom: "10px" }}>
            Signature
          </Typography>
          <SignatureCanvas onEnd={() => setDisableButton(false)} ref={sigCanvas} backgroundColor="#F1F1F1" canvasProps={{ width: 500, height: 200, className: 'sigCanvas' }}/>
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 12 }} spacing={2} style={{ marginTop: "10px" }} justifyContent="center" alignItems="center">
          <Button variant="contained" onClick={clearSignature}>Clear</Button>
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 12 }} spacing={2} style={{ marginTop: "10px" }} justifyContent="center" alignItems="center">
          <Typography variant="subtitle2" style={{ marginBottom: "10px" }}>
            Image
          </Typography>
          <StandardUploadInput
            alignItems="start"
            showPreview={true}
            onFileSelected={(files) => setImage(files[0])}
            handleSubmit={(files) => setImage(files[0])}
            initialImage={''}
            accept={'image/*'}
            uploadType={RESOURCE_TYPE.IMAGE}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 12 }} spacing={2} style={{ marginTop: "10px" }} justifyContent="center" alignItems="center">
          {
            !reportIsSaved ? (
              <Button variant="contained" style={{ marginRight: 10 }} onClick={() => saveReport()} disabled={disableButton}>Save</Button>
            ) : (
              <>
                <Button variant="contained" style={{ marginRight: 10 }} onClick={() => generateReport()} disabled={disableButton}>Download Report</Button>
                <Button variant="contained" onClick={() => generateReport(true)} disabled={disableButton}>Send Report to Primary Contact</Button>
              </>
            )
          }
        </Grid>
      </Grid>

    </>
  )
}

export default LocationReportPage;
